import { Fuelling } from "@/interfaces";
import api from "@/lib/axios";
import { DefaultGetRequestParams, DefaultResponse } from "./types";

interface FuellingResponse extends DefaultResponse<Fuelling, true> {}
interface FuellingSingleResponse extends DefaultResponse<Fuelling, false> {}
interface FuellingImportResponse extends DefaultResponse<
  {
    message: string;
    imported: number;
    errors: string[];
  },
  false
> {}

export interface IndexFuellingParams extends DefaultGetRequestParams {
  limit?: number;
  start_date?: string;
  end_date?: string;
}

export interface FuellingReportResponse {
  errors: boolean;
  data: {
    fuellings: Fuelling[];
    totals: {
      distance: number;
      literValueAverage: number;
      amount: number;
      liters: number;
    };
    chart: {
      datetime: string;
      value: number;
    }[];
    analysis: {
      best_price_provider: {
        common_gasoline: {
          name: string;
          liter_value_avg: number;
        };
        premium_gasoline: {
          name: string;
          liter_value_avg: number;
        };
        diesel: {
          name: string;
          liter_value_avg: number;
        };
      };
      fuel_consumption_avg: {
        common_gasoline: number;
        premium_gasoline: number;
        diesel: number;
      };
      price_avg_by_km: {
        common_gasoline: number;
        premium_gasoline: number;
        diesel: number;
      };
    };
  };
}

export interface FuellingReportParams {
  start_date: string;
  end_date: string;
  vehicle_id: number;
  client_provider_id?: number;
  categories?: string;
}

export interface VehiclesAnalysisProps {
  reportData: {
    vehicles: {
      plate: string;
      title: string;
      total: number;
      last_year_total: number;
    }[];
  };
  selectedYear: number;
}

export interface ExpenseData {
  name: string;
  current_year: number;
  previous_year: number;
}

export interface MonthsYearlyReport {
  name: string;
  previous_year: {
    total: number;
    categories: {
      [key: string]: number;
      fuelling: number;
      maintenance: number;
      repair: number;
      ticket: number;
      otherFees: number;
      insurance: number;
      licensing: number;
    };
  };
  current_year: {
    total: number;
    categories: {
      [key: string]: number;
      fuelling: number;
      maintenance: number;
      repair: number;
      ticket: number;
      otherFees: number;
      insurance: number;
      licensing: number;
    };
  };
}

export interface YearlyReportResponse {
  errors: boolean;
  data: {
    months: MonthsYearlyReport[];
    categories: {
      total: {
        current_year: number;
        previous_year: number;
        variation: number;
      };
      fuelling: {
        current_year: number;
        previous_year: number;
        variation: number;
      };
      maintenance: {
        current_year: number;
        previous_year: number;
        variation: number;
      };
      repair: {
        current_year: number;
        previous_year: number;
        variation: number;
      };
      licensing: {
        current_year: number;
        previous_year: number;
        variation: number;
      };
      ticket: {
        current_year: number;
        previous_year: number;
        variation: number;
      };
      otherFees: {
        current_year: number;
        previous_year: number;
        variation: number;
      };
      insurance: {
        current_year: number;
        previous_year: number;
        variation: number;
      };
    };
    vehicles: {
      [plate: string]: {
        id: number;
        plate: string;
        title: string;
        total: number;
        last_year_total: number;
        categories: {
          fuelling: number;
          maintenance: number;
          repair: number;
          ticket: number;
          otherFees: number;
          insurance: number;
          licensing: number;
        };
      };
    };
  };
}

export interface YearlyReportParams {
  year: number;
}

export interface VehiclesAnalysisProps {
  reportData: {
    vehicles: {
      plate: string;
      title: string;
      total: number;
      last_year_total: number;
    }[];
  };
  selectedYear: number;
}

export interface ExpensesAnalysisProps {
  reportData: YearlyReportResponse["data"];
  selectedYear: number;
}

export const fuellingService = {
  async index(
    {
      page = 1,
      per_page = 10,
      search,
      filters,
      limit,
      start_date,
      end_date,
    }: IndexFuellingParams = { page: 1, per_page: 10 },
  ) {
    const response = await api.get<FuellingResponse>("/fuelling", {
      params: {
        limit,
        start_date,
        end_date,
        page,
        per_page,
        search,
        ...filters,
      },
    });
    return response.data;
  },

  async show(id: number) {
    const response = await api.get<FuellingSingleResponse>(`/fuelling/${id}`);
    return response.data;
  },

  async store(data: Partial<Fuelling>) {
    const response = await api.post<FuellingSingleResponse>("/fuelling", data);
    return response.data;
  },

  async update(id: number | string, data: Partial<Fuelling>) {
    const response = await api.put<FuellingSingleResponse>(
      `/fuelling/${id}`,
      data,
    );
    return response.data;
  },

  async destroy(id: number | string) {
    const response = await api.delete<DefaultResponse<never>>(
      `/fuelling/${id}`,
    );
    return response.data;
  },

  async import(formData: FormData) {
    const response = await api.post<FuellingImportResponse>(
      "/fuelling/import",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  },

  async report(FuellingReportParams: FuellingReportParams) {
    const response = await api.get("/fuelling_report", {
      params: FuellingReportParams,
    });
    return response.data;
  },

  async buildPdf(FuellingReportParams: FuellingReportParams) {
    const response = await api.get("/fuelling_report_pdf", {
      params: FuellingReportParams,
      responseType: "blob",
    });
    return response.data;
  },

  async expensesReport(params: FuellingReportParams) {
    const response = await api.get("/expenses_report", {
      params,
    });
    return response.data;
  },

  async expensesReportPdf(params: FuellingReportParams) {
    const response = await api.get("/expenses_report_pdf", {
      params,
      responseType: "blob",
    });
    return response.data;
  },

  async yearlyReport(params: YearlyReportParams) {
    const response = await api.get("/expenses_report/yearly", {
      params,
    });
    return response.data;
  },
};
